/**
 * Structured API error.
 *
 * Lives in its own module (NOT in api.ts) on purpose: tests routinely
 * `vi.mock("../lib/api")`, and an `instanceof ApiError` check against a
 * mocked-away class export would silently break. Importing the class from
 * this un-mocked module keeps `instanceof` reliable everywhere.
 */

export class ApiError extends Error {
  readonly status: number;
  /** Parsed JSON `detail` field of the error body, when the body was JSON. */
  readonly detail: unknown;

  constructor(status: number, message: string, detail: unknown = undefined) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Structured 409 detail from `POST /api/payments` (and the order path) when
 * the lab's tier rules demand an operator review before payment. The SPA
 * must flip the checkout into a "Submit for review" call-to-action.
 */
export interface ReviewRequiredDetail {
  code: "review_required";
  tier?: string | null;
  message?: string | null;
}

export function reviewRequiredDetail(err: unknown): ReviewRequiredDetail | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.detail;
  if (
    d !== null &&
    typeof d === "object" &&
    (d as { code?: unknown }).code === "review_required"
  ) {
    return d as ReviewRequiredDetail;
  }
  return null;
}

/**
 * 409 from `POST /api/payments` when a fresh payment_intents reservation
 * already holds this quote (FSEC-1). Distinct from `review_required`.
 * The SPA must NOT treat this as a hard lock: same-user can release and
 * retry, but must not auto-release (another tab may have a live popup).
 */
export interface PaymentInProgressDetail {
  code: "payment_in_progress";
  message?: string | null;
}

/**
 * 502 from `POST /api/payments` when Revolut create-order (or preflight)
 * failed. Distinct from a raw API-Gateway 502. The SPA must show
 * ``message`` — never `502 : {"detail":"payment provider unavailable"}`.
 */
export interface PaymentProviderUnavailableDetail {
  code: "payment_provider_unavailable";
  reason: string;
  message: string;
  retryable: boolean;
  provider_status?: number | null;
}

export function paymentProviderUnavailableDetail(
  err: unknown,
): PaymentProviderUnavailableDetail | null {
  if (!(err instanceof ApiError) || err.status !== 502) return null;
  const d = err.detail;
  if (
    d !== null &&
    typeof d === "object" &&
    (d as { code?: unknown }).code === "payment_provider_unavailable"
  ) {
    const body = d as {
      reason?: unknown;
      message?: unknown;
      retryable?: unknown;
      provider_status?: unknown;
    };
    const message =
      typeof body.message === "string" && body.message.trim()
        ? body.message
        : "The payment provider is temporarily unavailable. Try again.";
    return {
      code: "payment_provider_unavailable",
      reason: typeof body.reason === "string" ? body.reason : "provider_error",
      message,
      retryable: body.retryable !== false,
      provider_status:
        typeof body.provider_status === "number" ? body.provider_status : null,
    };
  }
  // v0.1.10 mixed deploy: Lambda still returned a bare string.
  if (typeof d === "string" && d.includes("payment provider unavailable")) {
    return {
      code: "payment_provider_unavailable",
      reason: "provider_error",
      message: "The payment provider is temporarily unavailable. Try again.",
      retryable: true,
    };
  }
  if (
    typeof err.message === "string" &&
    err.message.includes("payment provider unavailable")
  ) {
    return {
      code: "payment_provider_unavailable",
      reason: "provider_error",
      message: "The payment provider is temporarily unavailable. Try again.",
      retryable: true,
    };
  }
  return null;
}

export function paymentInProgressDetail(
  err: unknown,
): PaymentInProgressDetail | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const d = err.detail;
  if (
    d !== null &&
    typeof d === "object" &&
    (d as { code?: unknown }).code === "payment_in_progress"
  ) {
    return d as PaymentInProgressDetail;
  }
  // Pre-#52 cloud returned a bare string detail. Keep matching so a mixed
  // deploy (old Lambda + new SPA) still offers recover.
  if (typeof d === "string" && d.includes("already in progress")) {
    return { code: "payment_in_progress", message: d };
  }
  if (typeof err.message === "string" && err.message.includes("already in progress")) {
    return { code: "payment_in_progress", message: err.message };
  }
  return null;
}
